<template>
    <section
        ref="section"
        class="slice slice-hero hero-section"
        :class="{'hero-section--hide-in-mobile': slice.primary.hideInMobile}"
        aria-label="hero"
    >
        <div v-if="slice.variation === 'heroWithShapes'" class="hero-with-shapes-wrapper">
            <HeroWithShapes
                :title="slice.primary.title"
                :cta-optional-link="slice.primary.CTAOptionalLink"
                :cta-optional-link-text="slice.primary.CTAOptionalLinkText"
                :background-shapes="slice.primary.backgroundShapes"
            />
        </div>

        <div
            v-else
            :class="{
                'hero-without-shapes': true,
                'hero-with-search': slice.variation === 'heroWithSearch',
            }"
        >
            <div class="slice-content text-left hero-wrapper">
                <div class="hero-without-search" :class="{'hero-with-form': slice.variation === 'heroWithForm'}">
                    <div class="hero-content">
                        <Breadscrumbs v-if="breadcrumbElements.length > 0" :breadcrumbs="breadcrumbElements" />
                        <MktMaltPrismicImage v-if="slice.primary.logo" :image-field="slice.primary.logo" class="hero-logo" />
                        <PrismicRichText :field="slice.primary.title" class="title" />
                        <div class="hero-details">
                            <PrismicRichText :field="slice.primary.description" class="description" />
                            <div v-if="slice.variation !== 'heroWithSearch'" class="ctas">
                                <VJoyButton
                                    v-if="isFilledKeyText(slice.primary.CTARequiredLinkText) && isFilledKeyText(slice.primary.CTARequiredLink)"
                                    :href="$withLocalePrefix(slice.primary.CTARequiredLink)"
                                    variant="primary"
                                    data-component-source="hero"
                                >
                                    {{ slice.primary.CTARequiredLinkText }}
                                </VJoyButton>
                                <VJoyButton
                                    v-if="isFilledKeyText(slice.primary.CTAOptionalLinkText) && isFilledKeyText(slice.primary.CTAOptionalLink)"
                                    :href="$withLocalePrefix(slice.primary.CTAOptionalLink)"
                                    variant="secondary"
                                    data-component-source="hero"
                                >
                                    {{ slice.primary.CTAOptionalLinkText }}
                                </VJoyButton>
                            </div>
                        </div>
                    </div>
                    <div v-if="slice.variation !== 'heroWithForm' && showHeroImage" class="image">
                        <LazyDiaporama
                            v-if="[slice.primary.image, slice.primary.image2, slice.primary.image3].filter((image) => image && image.url).length > 1"
                            :interval="7000"
                            :images="[slice.primary.image, slice.primary.image2, slice.primary.image3].filter((image) => image && image.url)"
                            :sizes="imageSizes"
                        />
                        <MktMaltPrismicImage v-else :image-field="slice.primary.image" :sizes="imageSizes" loading="eager" />
                    </div>
                    <div v-if="slice.variation === 'heroWithForm'" class="card-form">
                        <LazyHubspotForm
                            :region="slice.primary.hubspotRegion!"
                            :portal-id="slice.primary.hubspotPortalId!"
                            :form-id="slice.primary.hubspotFormId!"
                            :salesforce-id="slice.primary.salesforceId!"
                        />
                    </div>
                </div>
                <div v-if="slice.variation === 'heroWithSearch'" class="search">
                    <LazySearchPanel
                        :search-tab="searchLinkText"
                        :search-field-category="slice.primary.categoryLabel"
                        :search-field-location="slice.primary.locationLabel"
                        :search-location-remote-country-label="slice.primary.remoteInCountryLabel"
                        :search-submit="searchLinkText"
                        :search-submit-link="slice.primary.searchLink"
                        :ai-search-tab="slice.primary.aiSearchLinkText"
                        :ai-search-tab-link="slice.primary.aiSearchLink"
                        :ai-entity-detection-enabled="$ff('one-entry-point-improvements-unlogged-homepage')"
                        :reassurance-items="slice.items"
                        brief-origin="home_unlogged"
                        page-source="home_unlogged_try_ai_search"
                        origin="USER_PROMPT_HOMEPAGE_UNLOGGED"
                    />
                </div>
            </div>
        </div>
        <div class="slice-content reinsurance-wrapper">
            <ReinsurancePanel
                v-if="slice.variation === 'heroWithSearch'"
                :items="slice.items"
                :trustpilot-data-locale="`${slice.primary.trustpilotDataLocale}`"
                :trustpilot-data-template-id="`${slice.primary.trustpilotDataTemplateId}`"
                :trustpilot-data-businessunit-id="`${slice.primary.trustpilotDataBusinessunitId}`"
                :trustpilot-link="`${slice.primary.trustpilotLink}`"
                :hide-trustpilot="!!slice.primary.hideTrustpilot"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, useTemplateRef, defineAsyncComponent} from 'vue';
    import {LinkType} from '@prismicio/types';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {HeroSliceSlice, HeroSliceSliceDefaultItem} from '@prismicio-types';
    import {MktMaltPrismicImage} from '#components';
    import {usePrismicTypeGuards, usePrismicFieldHelpers, useComponentTracking, useTranslation} from '#imports';
    import {useABTest} from '@malt/feature-flags';
    import {useMediaQuery} from '@malt/utils';
    import {VJoyButton} from '@maltjoy/core-vue';
    import Breadscrumbs from '~/components/Breadscrumbs.vue';
    import HeroWithShapes from '~/components/HeroWithShapes.vue';
    import ReinsurancePanel from '~/components/ReinsurancePanel.vue';
    import errorHelpers from '~/composables/errorHelpers';

    const props = defineProps(getSliceComponentProps<HeroSliceSlice>());
    const {t} = useTranslation();
    const {isLink} = usePrismicFieldHelpers();
    const {isFilledKeyText} = usePrismicTypeGuards();
    const section = useTemplateRef('section');

    const LazyDiaporama = defineAsyncComponent(() => import('~/components/Diaporama.vue'));
    const LazyHubspotForm = defineAsyncComponent(() => import('~/components/HubspotForm.vue'));
    const LazySearchPanel = defineAsyncComponent(() => import('@search/components/SearchPanel.vue'));

    const {matchMedia} = useMediaQuery();
    const showHeroImage = computed(() => !matchMedia.value.max.Sm);

    const breadcrumbElements =
        props.slice.variation !== 'default'
            ? []
            : props.slice.items.reduce((cleanedLinks: HeroSliceSliceDefaultItem[], link: HeroSliceSliceDefaultItem) => {
                  if (!link.breadscrumbText) {
                      return cleanedLinks;
                  }
                  if (isLink(link.breadscrumbText, link.breadscrumbLink)) {
                      return [...cleanedLinks, link];
                  }
                  // If the breadcrumb link is an Any link, add it to the list to display as text
                  if (link.breadscrumbLink && link.breadscrumbLink.link_type === LinkType.Any) {
                      return [...cleanedLinks, link];
                  }
                  return cleanedLinks;
              }, []);

    const imageSizes = 'tablet:636px';

    const {getVariation} = useABTest();

    const searchLinkText = computed(() => {
        if (props.slice.variation !== 'heroWithSearch') {
            return;
        }

        return getVariation('keyword-search-cta-label-change') === 'treatment'
            ? t('search.advanced.ai.entrypoint.mode.keyword.search.switch.button.browse')
            : props.slice.primary.searchLinkText;
    });

    onMounted(() => {
        const {getPreviewErrorHandler, throwIfPrimaryPropertiesAreInvalid} = errorHelpers();
        const errorHandler = getPreviewErrorHandler();
        if (errorHandler.enabled) {
            if (props.slice.variation === 'heroWithSearch') {
                throwIfPrimaryPropertiesAreInvalid(
                    errorHandler.toast,
                    props.slice.primary,
                    [
                        {name: 'title', type: 'field'},
                        {name: 'description', type: 'field'},
                    ],
                    'hero search',
                );

                return;
            }

            if (props.slice.variation === 'default') {
                throwIfPrimaryPropertiesAreInvalid(
                    errorHandler.toast,
                    props.slice.primary,
                    [
                        {name: 'title', type: 'field'},
                        {name: 'description', type: 'field'},
                        {name: 'CTARequiredLink', type: 'rawText'},
                        {name: 'CTARequiredLinkText', type: 'rawText'},
                    ],
                    'hero',
                );
            }
        }
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $flex-gap: 48px;

    .hero-section {
        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: block;
            }
        }

        .hero-without-shapes {
            background-image: url('~/assets/svg/home-page-hero-shapes.svg');
            background-repeat: no-repeat;
            background-position: top right;
            background-size: contain;
            padding-top: 16px;

            @include flex-column-justify-center;

            justify-content: flex-start;
            align-items: center;
            position: relative;
            top: -15px;

            @include mq.screen_sm(max) {
                background-image: url('~/assets/svg/home-page-hero-mobile-shapes.svg');
                padding-top: 21px;
            }

            .hero-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: center;
                width: 100%;
            }

            .hero-without-search {
                width: 100%;
                display: flex;
                align-items: center;
                gap: $flex-gap;
            }

            .hero-content {
                width: 50%;

                @include mq.screen_sm(max) {
                    width: 100%;
                }
            }

            .image {
                width: 50%;
                height: auto;

                img {
                    width: 100%;
                    height: auto;
                    border-radius: var(--joy-core-radius-4);
                    vertical-align: middle;
                }

                @include mq.screen_sm(max) {
                    display: none;
                }

                @media (max-height: 800px) {
                    img {
                        width: 500px;
                    }
                }
            }

            .hero-with-form {
                flex-direction: column;
                gap: 44px;

                @include mq.screen_md(min) {
                    flex-direction: row;
                    gap: $flex-gap;
                }

                .card-form {
                    flex: none;
                    background: var(--joy-color-background-default);
                    width: 100%;
                    min-width: 40px;
                    max-width: 550px;
                    margin: o auto;
                    border-radius: var(--joy-core-radius-4);
                    padding: var(--joy-core-spacing-6) var(--joy-core-spacing-5);
                    box-shadow: 0 var(--joy-core-spacing-1) var(--joy-core-spacing-5) 0 rgb(51 51 48 / 20%);
                    height: auto;

                    @include mq.screen_md(min) {
                        width: calc(50% - $flex-gap);
                        padding: var(--joy-core-spacing-8) var(--joy-core-spacing-6);
                    }
                }
            }

            .search {
                margin-top: 48px;
                width: 100%;

                .search-card {
                    width: 100%;
                }
            }

            .title h1 {
                margin-bottom: 30px;

                @include primary-title;

                @include mq.screen_sm(max) {
                    margin-bottom: 24px;
                }
            }

            .description {
                font-weight: var(--joy-font-weight-normal);
                font-size: var(--joy-font-size-primary-600);
                line-height: var(--joy-line-height-large);
                color: var(--joy-color-text-default);

                a:hover {
                    text-decoration: underline;
                }

                a {
                    color: var(--joy-color-secondary-30);

                    @include mq.screen_sm {
                        color: inherit;
                        text-decoration: underline;
                    }
                }
            }

            .ctas {
                display: flex;
                gap: 20px;
                flex-wrap: wrap;
                margin-top: 48px;

                .joy-button {
                    width: 100%;

                    @include mq.screen_sm(min) {
                        width: auto;
                    }
                }
            }
        }

        .hero-with-search {
            @include mq.screen_sm(max) {
                background-image: url('~/assets/svg/hero-with-search-shapes.svg');
                background-position: bottom center;
                background-size: cover;
                color: var(--joy-color-neutral-0);

                .title h1,
                .hero-details * {
                    color: var(--joy-color-neutral-0);
                }
            }
        }

        .hero-logo {
            max-width: 150px;
            height: auto;
            margin-bottom: var(--joy-core-spacing-4);

            @include mq.screen_md(min) {
                max-width: 250px;
                margin-bottom: var(--joy-core-spacing-8);
            }
        }

        .reinsurance-wrapper {
            padding-top: 0;
            padding-bottom: 0;
        }
    }
</style>
